/**
 * 游戏音效工具。
 *
 * 音效来源分两类：
 * - 固定 wav 文件：发牌、三道翻牌、开始比牌、打枪、牌型播报；
 * - Web Audio 合成：倒计时 3/2/1 和命中提示这类短促反馈。
 *
 * 所有播放入口都会遵守 isMuted，组件层不需要重复判断静音。
 */

let audioCtx = null
let isMuted = localStorage.getItem('soundMuted') === 'true'

export function getMuted() {
  return isMuted
}

export function setMuted(value) {
  isMuted = value
  localStorage.setItem('soundMuted', value ? 'true' : 'false')
}

function getAudioContext() {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)()
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume()
  }
  return audioCtx
}

// 预加载外部音频文件
const dealAudio = new Audio(require('@/assets/sounds/shuffle_cards.wav'))
const gunAudio = new Audio(require('@/assets/sounds/pistol.wav'))
const sandao1Audio = new Audio(require('@/assets/sounds/sandao1.wav'))
const sandao2Audio = new Audio(require('@/assets/sounds/sandao2.wav'))
const sandao3Audio = new Audio(require('@/assets/sounds/sandao3.wav'))
const compareStartAudio = new Audio(require('@/assets/sounds/开始比牌.wav'))
const homeRunAudio = new Audio(require('@/assets/sounds/全垒打.wav'))

// 牌型名必须和 handEvaluator.js 中 HAND_RANKS.name 返回值一致。
// 如果音频文件名变化，只需要修改这里的映射。
const handTypeAudios = {
  散牌: new Audio(require('@/assets/sounds/散牌.wav')),
  一对: new Audio(require('@/assets/sounds/一对.wav')),
  两对: new Audio(require('@/assets/sounds/两对.wav')),
  三条: new Audio(require('@/assets/sounds/三条.wav')),
  顺子: new Audio(require('@/assets/sounds/顺子.wav')),
  同花: new Audio(require('@/assets/sounds/同花.wav')),
  葫芦: new Audio(require('@/assets/sounds/葫芦.wav')),
  铁支: new Audio(require('@/assets/sounds/铁支.wav')),
  同花顺: new Audio(require('@/assets/sounds/同花顺.wav')),
  五同: new Audio(require('@/assets/sounds/五同.wav')),
  乌龙: new Audio(require('@/assets/sounds/散牌.wav'))
}

// 统一播放辅助函数：重置并播放
function playAudio(audio) {
  if (isMuted) return
  try {
    audio.currentTime = 0
    audio.play().catch(() => {})
  } catch (e) {
    // 静默失败，不影响游戏
  }
}

/**
 * 发牌音效：播放 shuffle_cards.wav
 */
export function playDealSound() {
  playAudio(dealAudio)
}

/**
 * 比牌翻牌音效：三道分别播放 sandao1/2/3.wav
 * @param {number} round - 1:头道, 2:中道, 3:尾道
 */
export function playRevealSound(round) {
  const map = {
    1: sandao1Audio,
    2: sandao2Audio,
    3: sandao3Audio
  }
  const audio = map[round]
  if (audio) {
    playAudio(audio)
  }
}

/**
 * 牌型语音播报：播放 sounds 目录下的牌型语音文件。
 * @param {string} handType - 如：散牌、两对、同花顺
 */
export function playHandTypeVoice(handType) {
  if (isMuted || !handType) return
  const audio = handTypeAudios[handType]
  if (audio) playAudio(audio)
}

/**
 * 打枪音效：播放 pistol.wav
 */
export function playGunSound() {
  playAudio(gunAudio)
}

/**
 * 全垒打语音：播放 全垒打.wav
 */
export function playHomeRunSound() {
  playAudio(homeRunAudio)
}

/**
 * 比牌倒计时音效：3/2/1 使用递进提示音
 * @param {number} count - 当前倒计时数字
 */
export function playCompareCountdownSound(count) {
  if (isMuted) return
  try {
    const ctx = getAudioContext()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    const frequencies = {
      3: 520,
      2: 640,
      1: 780
    }

    osc.type = 'sine'
    osc.frequency.setValueAtTime(frequencies[count] || 620, ctx.currentTime)

    gain.gain.setValueAtTime(0.001, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18)

    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.start(ctx.currentTime)
    osc.stop(ctx.currentTime + 0.2)
  } catch (e) {
    // 静默失败
  }
}

/**
 * 打枪击中/弹孔音效：金属撞击声（合成）
 */
export function playHitSound() {
  if (isMuted) return
  try {
    const ctx = getAudioContext()

    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'square'
    osc.frequency.setValueAtTime(800, ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(300, ctx.currentTime + 0.1)

    gain.gain.setValueAtTime(0.08, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12)

    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.start(ctx.currentTime)
    osc.stop(ctx.currentTime + 0.12)
  } catch (e) {
    // 静默失败
  }
}

/**
 * 比牌开始音效：倒计时结束后播放“开始比牌”
 */
export function playCompareStartSound() {
  playAudio(compareStartAudio)
}

/**
 * 报道牌提示音。一条龙更高、更长，其余特殊牌型用短促上扬音。
 */
export function playSpecialHandSound(type) {
  if (isMuted) return
  try {
    const ctx = getAudioContext()
    const now = ctx.currentTime
    const strong = type === 'DRAGON'
    const notes = strong ? [523, 659, 784, 1046] : [440, 554, 659]
    notes.forEach((freq, index) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      const start = now + index * 0.09
      osc.type = strong ? 'triangle' : 'sine'
      osc.frequency.setValueAtTime(freq, start)
      gain.gain.setValueAtTime(0.001, start)
      gain.gain.exponentialRampToValueAtTime(strong ? 0.16 : 0.1, start + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.28)
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.start(start)
      osc.stop(start + 0.3)
    })
  } catch (e) {
    // 静默失败
  }
}
